import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import PdfEditPage from '../components/PdfEditPage';
import s, { PdfEditResponseStruct } from '../utils/struct';
import useApiRequest from '../utils/useApiRequest';
import { apiUrl } from '../utils/apiRequest';
import getSelectedPages from '../utils/getSelectedPages';
import getErrorName from '../utils/getErrorName';
import { sendEvent } from '../utils/analytics';

// Delete Pages переиспользует рабочий флоу Split: удаление страниц в вьюере
// пишется в pdfFile.deletions, а getSelectedPages отдаёт «все страницы минус
// удалённые», поэтому /api/pdf/split с separate:false собирает один PDF без
// удалённых страниц. Отдельного /api/pdf/delete-pages на бэке нет.
const PageDeletePages = () => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'delete-pages']);
  const [fileTable, setFileTable] = useState(null);

  const {
    sendApiRequest,
    processing,
    error,
  } = useApiRequest({ url: '/api/pdf/split' });

  const handleSubmit = useCallback(({ pdfFiles }) => {
    if (processing) {
      return;
    }

    sendApiRequest({
      data: {
        files: pdfFiles.map((pdfFile) => ({
          name: pdfFile.file.file.name,
          filename: pdfFile.file.hash,
          pages: getSelectedPages(pdfFile),
          password: pdfFile.file.password,
          separate: false,
        })),
      },
      validateResponse(response) {
        s.assert(response.data, PdfEditResponseStruct);
      },
      onSuccess(response, time) {
        sendEvent({
          category: 'pdf-delete-pages',
          action: 'success',
          label: `${time}:${pdfFiles.length}`,
        });

        const firstFile = pdfFiles[0].file;
        const firstResultFiles = response.data.result?.[firstFile.hash]?.files;
        const { url, size } = firstResultFiles[0];

        setFileTable({
          fileType: 'pdf',
          fileName: firstFile.file.name,
          fileSize: size,
          fileUrl: apiUrl(url),
          compressable: false,
        });
      },
      onError(error, time) {
        sendEvent({
          category: 'pdf-delete-pages',
          action: 'error',
          label: `${getErrorName(error)}:${time}:${pdfFiles.length}`,
        });
      },
    });
  }, [processing, sendApiRequest]);

  const meta = useMemo(() => ({
    title: t('delete-pages:meta.title'),
    description: t('delete-pages:meta.description'),
  }), [t]);

  const intro = useMemo(() => ({
    title: t('delete-pages:intro.title'),
    text: t('delete-pages:intro.text'),
  }), [t]);

  const dropzone = useMemo(() => ({
    options: {
      multiple: false,
      maxFiles: 1,
    },
  }), []);

  const howTo = useMemo(() => ({
    title: t('delete-pages:howto.title'),
    steps: t('delete-pages:howto.steps', { returnObjects: true }),
  }), [t]);

  const features = useMemo(() => ({
    content: t('delete-pages:features.content', { returnObjects: true }),
  }), [t]);

  const faq = useMemo(() => ({
    items: t('delete-pages:faq.items', { returnObjects: true }),
  }), [t]);

  const pdfViewer = useMemo(() => ({
    title: t('common:nav.delete-pages'),
    viewType: 'pages',
    showViewSwitcher: false,
    separatable: false,
    selectable: false,
    deletablePages: true,
    // пока ни одна страница не удалена, отправлять нечего — вернулся бы оригинал
    requireChange: 'deletions',
    processing,
  }), [t, processing]);

  const actionsGrid = useMemo(() => ({
    rotate: true,
    unlock: true,
    split: true,
    compress: true,
    pdf2word: true,
    pdf2excel: true,
    pdf2jpg: true,
    pdf2png: true,
    pdf2ppt: true,
  }), []);

  return (
    <PdfEditPage
      meta={meta}
      breadcrumb={t('common:nav.delete-pages')}
      intro={intro}
      dropzone={dropzone}
      features={features}
      faq={faq}
      pdfViewer={pdfViewer}
      fileTable={fileTable}
      actionsGrid={actionsGrid}
      onSubmit={handleSubmit}
      submitError={error}
      howTo={howTo}
    />
  );
};

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...await serverSideTranslations(locale, [
      'common',
      'convert-and-edit',
      'delete-pages',
    ]),
  },
});

export default PageDeletePages;
